import { useCallback, useEffect, useMemo } from 'react';

import { getCourseIds, getCoursesByCategories } from '@/_generic';
import { useGetLeadQuery } from '@/_generic/actions/apis';
import { weeksSum } from '@/_generic/calculations';
import type { TAtpCourseData, TAtpOptionalCourseData, TLeadModelData } from '@/_models';
import { atpOptionalStaticData, checkoutStateModel, leadModelData } from '@/_models';
import { Routes, useUrlParams } from '@/_navigation';

import { useCacheContext } from '../../cache';

/**
 * @description gets lead_id from url and makes API call to get lead data
 * checks order_data, sets data to state
 *
 * @returns leadId, leadInfo, courses data and fetching information
 */

export function useBridgePlan(polling = false, skip = false) {
  const { paramSummaryId, paramCampusId, isPublic, paramLeadId, moveToLink, isDiscountCheckoutMode, tcValue } =
    useUrlParams();

  const { initLeadData, overrideGlobalData } = useCacheContext();

  const { isFetching, isLoading, isSuccess, isError, data, refetch } = useGetLeadQuery(
    { lead_id: paramLeadId, cpId: paramSummaryId, campusId: paramCampusId, tcValue: tcValue ?? undefined },
    { skip, pollingInterval: polling ? 5000 : 0, refetchOnMountOrArgChange: polling }
  );

  const {
    is_climb_eligible,
    campus_to_program,
    id,
    name,
    first_name,
    email,
    mphone,
    course_order_info,
    state,
    account_charge,
  } = (isSuccess ? data : leadModelData) as TLeadModelData;

  const {
    courses_list_taken_at_achieve,
    courses_list_taken_at_college,
    optional_courses,
    program,
    id: collegeId,
    college_name,
    college_website,
    campus_state,
    campus_zipcode,
  } = campus_to_program;

  const { order_data } = course_order_info || {};

  const {
    courses,
    entranceExamCourses,
    licensingExamCourses,
    optionalSelectableCourses,
    odtSelectableCourses,
    atpSelectableCourses,
    fundamentalCoursesOnly,
    atpSelectableCoursesOnly,
    courseIdsAsSelected,
    planPrepCourseIds,
    planAddonCourseIds,
    planOdtCourseIds,
    planFundamentalCourseIds,
  } = useMemo(() => {
    const coursesByCategories = getCoursesByCategories(optional_courses, courses_list_taken_at_achieve);
    const courseIdsByCategories = getCourseIds(coursesByCategories);

    return { ...coursesByCategories, ...courseIdsByCategories };
  }, [optional_courses, courses_list_taken_at_achieve]);

  const weeksSaving = useMemo(
    () => weeksSum([...atpSelectableCourses, ...entranceExamCourses, ...licensingExamCourses]),
    [atpSelectableCourses, entranceExamCourses, licensingExamCourses]
  );

  const leadInfo = useMemo(
    () => ({
      leadId: id,
      leadName: `${first_name} ${name}`,
      firstName: first_name,
      lastName: name,
      email,
      mobilePhone: mphone,
      state: state || '',
      programType: program,
      collegeId,
      collegeName: college_name,
      collegeWebsite: college_website ?? '—',
      collegeSubInfo: (campus_state || '') + (campus_zipcode ? `, ${campus_zipcode}` : ''),
      timeSaved: `${weeksSaving}`,
    }),
    [
      campus_state,
      campus_zipcode,
      collegeId,
      college_name,
      college_website,
      email,
      first_name,
      id,
      mphone,
      name,
      program,
      state,
      weeksSaving,
    ]
  );

  const restore = useCallback(
    (redirection = true) => {
      // INIT DISCOUNT MODE
      // RESETTING DATA
      let tempOrderData = order_data;

      if (order_data && isDiscountCheckoutMode && order_data?.paymentOptions) {
        let { paymentOptions, checkout } = order_data;
        const { backup } = order_data;
        const { hasDiscountModeProcessInitialized } = paymentOptions;
        if (!hasDiscountModeProcessInitialized) {
          paymentOptions = { ...paymentOptions, hasDiscountModeProcessInitialized: true };
          checkout = {
            ...checkout,
            hasReviewedTerms: false,
            termsReviewedByCustomer: false,
            password: checkoutStateModel.password,
            card_1: { ...checkoutStateModel.card_1 },
            card_2: { ...checkoutStateModel.card_2 },
          };

          const backupData = JSON.parse(backup || '[]');
          backupData.push(data);

          tempOrderData = {
            ...tempOrderData,
            paymentOptions,
            checkout,
            isLocked: false,
            backup: JSON.stringify(backupData),
          };
        }
      }

      const serverLeadData = JSON.stringify(tempOrderData);

      const _planPrepCourseIds = atpSelectableCourses.map((course) => course.id);
      const _planAddonCourseIds = optionalSelectableCourses.map((course) => course.id);
      const _planOdtCourseIds = odtSelectableCourses.map((course) => course.id);
      const _planFundamentalCourseIds = fundamentalCoursesOnly.map((course) => course.id);

      initLeadData({
        serverLeadData,
        planPrepCourseIds: _planPrepCourseIds,
        planAddonCourseIds: _planAddonCourseIds,
        planOdtCourseIds: _planOdtCourseIds,
        planFundamentalCourseIds: _planFundamentalCourseIds,
        isAutomatedRedirectionAllowed: redirection,
      });
    },
    [
      order_data,
      isDiscountCheckoutMode,
      atpSelectableCourses,
      optionalSelectableCourses,
      odtSelectableCourses,
      fundamentalCoursesOnly,
      initLeadData,
    ]
  );

  useEffect(() => {
    const res = [
      Routes.BRIDGE_PLAN.link,
      Routes.COLLEGE_INFO.link,
      Routes.COLLEGE_REQ.link,
      Routes.COLLEGE_PROG_REQ.link,
    ];

    if (isPublic) {
      for (let i = 0; i < res.length; i++) {
        if (window.location.pathname === `${res[i]}/${paramLeadId}/`) {
          return;
        }
      }

      moveToLink(Routes.BRIDGE_PLAN.link as string);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  return {
    showPrices: true,

    refetch,
    isLoading,
    isFetching,
    isSuccess,
    isError,

    leadInfo,

    atpSelectableCourses,
    atpSelectableCoursesOnly: atpSelectableCoursesOnly as TAtpCourseData[],
    fundamentalCoursesOnly: fundamentalCoursesOnly as TAtpCourseData[],
    preSelectedOptionalCourses: atpOptionalStaticData,

    entranceExamCourses: entranceExamCourses ?? ([] as TAtpOptionalCourseData[]),
    licensingExamCourses: licensingExamCourses ?? ([] as TAtpOptionalCourseData[]),
    odtSelectableCourses: odtSelectableCourses ?? ([] as TAtpOptionalCourseData[]),

    optionalSelectableCourses,
    collegeCourses: courses_list_taken_at_college ?? [],
    order_data: order_data || {},

    courses,
    courseIdsAsSelected,

    planPrepCourseIds,
    planAddonCourseIds,
    planOdtCourseIds,
    planFundamentalCourseIds,

    account_charge: account_charge || 0,
    is_climb_eligible,

    restore,
    overrideGlobalData,
  };
}

export type TUseBridgePlan = ReturnType<typeof useBridgePlan>;
